import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PageResponse } from '../../contactos/models/contacto.model';
import {
  AddCampaignListRequest,
  AddExcludedSegmentRequest,
  AddManualEmailsRequest,
  BatchAddSummary,
  BatchContactsRequest,
  CampaignExcludedContact,
  CampaignExcludedSegment,
  CampaignList,
  CampaignManualRecipient,
  CampaignMetrics,
  CampaignPreviewCount,
  CampaignRecipient,
  CampaignRecipientsParams,
  ManualEmailRecipient,
  MarketingCampaign,
  MarketingCampaignRequest,
  SendNowSummary,
} from '../models/marketing-campaign.model';

@Injectable({ providedIn: 'root' })
export class MarketingCampaignApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}api/marketing/campaigns`;

  list(): Observable<MarketingCampaign[]> {
    return this.http.get<MarketingCampaign[]>(this.base);
  }

  getById(id: number): Observable<MarketingCampaign> {
    return this.http.get<MarketingCampaign>(`${this.base}/${id}`);
  }

  create(req: MarketingCampaignRequest): Observable<MarketingCampaign> {
    return this.http.post<MarketingCampaign>(this.base, req);
  }

  update(id: number, req: MarketingCampaignRequest): Observable<MarketingCampaign> {
    return this.http.put<MarketingCampaign>(`${this.base}/${id}`, req);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  /**
   * Mueve un correo a una carpeta (`folderId`) o lo saca de toda carpeta
   * (`folderId: null`). Endpoint dedicado: funciona en CUALQUIER estado
   * (DRAFT/SCHEDULED/SENDING/SENT/CANCELLED/FAILED).
   *
   * NO usar `update()` para mover de carpeta: el PUT valida editabilidad
   * (400 en SENDING/SENT — Inc 40) y su request completo puede borrar los
   * adjuntos si se omite `attachments`.
   */
  moveToFolder(id: number, folderId: number | null): Observable<MarketingCampaign> {
    return this.http.patch<MarketingCampaign>(`${this.base}/${id}/folder`, { folderId });
  }

  cancel(id: number): Observable<MarketingCampaign> {
    return this.http.post<MarketingCampaign>(`${this.base}/${id}/cancel`, {});
  }

  sendNow(id: number): Observable<SendNowSummary> {
    return this.http.post<SendNowSummary>(`${this.base}/${id}/send-now`, {});
  }

  /**
   * Marca FAILED una campaña atascada en SENDING/encolado (destrabar).
   * 400 si ya está en un estado final. Devuelve la campaña con status="FAILED".
   */
  markFailed(id: number): Observable<MarketingCampaign> {
    return this.http.post<MarketingCampaign>(`${this.base}/${id}/mark-failed`, {});
  }

  /** Reencola los destinatarios que fallaron en una campaña ya enviada (SENT/FAILED). */
  resendFailed(id: number): Observable<SendNowSummary> {
    return this.http.post<SendNowSummary>(`${this.base}/${id}/resend-failed`, {});
  }

  // Listas (segmentos vinculados)
  listLists(id: number): Observable<CampaignList[]> {
    return this.http.get<CampaignList[]>(`${this.base}/${id}/lists`);
  }

  addList(id: number, req: AddCampaignListRequest): Observable<CampaignList> {
    return this.http.post<CampaignList>(`${this.base}/${id}/lists`, req);
  }

  removeList(id: number, segmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/lists/${segmentId}`);
  }

  // Métricas agregadas (dashboard)
  getMetrics(id: number): Observable<CampaignMetrics> {
    return this.http.get<CampaignMetrics>(`${this.base}/${id}/metrics`);
  }

  // Listado paginado de recipients con tracking
  listRecipients(id: number, params: CampaignRecipientsParams = {}): Observable<PageResponse<CampaignRecipient>> {
    let httpParams = new HttpParams();
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.page !== undefined) httpParams = httpParams.set('page', String(params.page));
    if (params.size !== undefined) httpParams = httpParams.set('size', String(params.size));
    return this.http.get<PageResponse<CampaignRecipient>>(`${this.base}/${id}/recipients`, { params: httpParams });
  }

  // ===== Manual recipients =====
  listManualRecipients(id: number): Observable<CampaignManualRecipient[]> {
    return this.http.get<CampaignManualRecipient[]>(`${this.base}/${id}/manual-recipients`);
  }

  addManualRecipients(id: number, contactIds: number[]): Observable<BatchAddSummary> {
    const body: BatchContactsRequest = { contactIds };
    return this.http.post<BatchAddSummary>(`${this.base}/${id}/manual-recipients`, body);
  }

  /**
   * Agrega destinatarios directos por EMAIL arbitrario (registrado o no como
   * contacto). Solo DRAFT/SCHEDULED. Idempotente (duplicados → alreadyPresent).
   */
  addManualEmails(id: number, recipients: ManualEmailRecipient[]): Observable<BatchAddSummary> {
    const body: AddManualEmailsRequest = { recipients };
    return this.http.post<BatchAddSummary>(`${this.base}/${id}/manual-recipients/emails`, body);
  }

  removeManualRecipient(id: number, contactId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/manual-recipients/${contactId}`);
  }

  /**
   * Quita un destinatario manual por su id de FILA (sirve tanto para emails
   * sueltos —idContact null— como para contactos vinculados). Solo DRAFT/SCHEDULED.
   */
  removeManualRecipientById(id: number, recipientId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/manual-recipients/by-id/${recipientId}`);
  }

  clearManualRecipients(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/manual-recipients`);
  }

  // ===== Excluded contacts =====
  listExcludedContacts(id: number): Observable<CampaignExcludedContact[]> {
    return this.http.get<CampaignExcludedContact[]>(`${this.base}/${id}/excluded-contacts`);
  }

  addExcludedContacts(id: number, contactIds: number[]): Observable<BatchAddSummary> {
    const body: BatchContactsRequest = { contactIds };
    return this.http.post<BatchAddSummary>(`${this.base}/${id}/excluded-contacts`, body);
  }

  removeExcludedContact(id: number, contactId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/excluded-contacts/${contactId}`);
  }

  clearExcludedContacts(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/excluded-contacts`);
  }

  // ===== Excluded segments =====
  listExcludedSegments(id: number): Observable<CampaignExcludedSegment[]> {
    return this.http.get<CampaignExcludedSegment[]>(`${this.base}/${id}/excluded-segments`);
  }

  addExcludedSegment(id: number, req: AddExcludedSegmentRequest): Observable<CampaignExcludedSegment> {
    return this.http.post<CampaignExcludedSegment>(`${this.base}/${id}/excluded-segments`, req);
  }

  removeExcludedSegment(id: number, segmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/excluded-segments/${segmentId}`);
  }

  // ===== Preview =====
  previewCount(id: number): Observable<CampaignPreviewCount> {
    return this.http.get<CampaignPreviewCount>(`${this.base}/${id}/preview-count`);
  }
}
